import { useContext, useEffect, useState } from "react";
import {
  fetchProtectedMediaObjectUrl,
  isProtectedMediaSrc,
  ProtectedMediaCacheContext,
} from "../display-editor/ProtectedMediaCacheContext";

/**
 * Resolves cached layout media URLs (image/video) into blob URLs.
 *
 * For private `/user-layouts/*` endpoints, the browser cannot attach Bearer
 * headers directly to `<img src>` / `<video src>`. For public runtime relay
 * assets, we still fetch once and expose a blob URL so the renderer reuses the
 * media for the lifetime of the mounted page.
 *
 * @param src - Original media URL.
 * @returns A direct media source usable in `<img src>` or `<video src>`.
 */
export function useProtectedMediaSrc(src?: string): string {
  const mediaCache = useContext(ProtectedMediaCacheContext);
  const [resolvedSrc, setResolvedSrc] = useState(() =>
    src && isProtectedMediaSrc(src)
      ? (mediaCache?.getObjectUrl(src) ?? "")
      : (src ?? ""),
  );

  useEffect(() => {
    if (!src) {
      setResolvedSrc("");
      return;
    }
    const isProtectedMedia = isProtectedMediaSrc(src);
    if (!isProtectedMedia) {
      setResolvedSrc(src);
      return;
    }

    const cachedObjectUrl = mediaCache?.getObjectUrl(src);
    if (cachedObjectUrl) {
      setResolvedSrc(cachedObjectUrl);
      return;
    }

    let objectUrl: string | null = null;
    let isCancelled = false;

    const resolveSource = mediaCache
      ? mediaCache.preload(src)
      : fetchProtectedMediaObjectUrl(src).then((localObjectUrl) => {
          objectUrl = localObjectUrl;
          return localObjectUrl;
        });

    resolveSource
      .then((nextSrc) => {
        if (isCancelled) return;
        setResolvedSrc(nextSrc);
      })
      .catch(() => {
        if (isCancelled) return;
        setResolvedSrc("");
      });

    return () => {
      isCancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [mediaCache, mediaCache?.version, src]);

  return resolvedSrc;
}
